/**
 * StockLot — frontend mirror of the NestJS StockLot model (Phase 4c).
 *
 * A StockLot represents the on-hand quantity of a specific Part at a
 * specific (vessel, location). Every Part can be stocked in multiple
 * lots — a filter cartridge might live in the engine room main locker
 * AND a backup in the bridge spares locker.
 *
 * Quantity is managed by an APPEND-ONLY ledger of StockMovement rows;
 * `quantityOnHand` is a denormalised cache updated atomically in the
 * same DB transaction as the movement insert. The frontend therefore
 * only ever READS quantityOnHand — mutations always go through one of
 * the four movement endpoints (receipt / consumption / adjustment /
 * transfer).
 *
 * Threshold inheritance:
 *   - minStockOverride === null  → inherit Part.minStockGlobal
 *   - minStockOverride === N     → use N specifically for this lot
 *   (idem for maxStockOverride)
 *
 * "Low stock" is computed client-side on every list/detail render:
 *   effectiveMin = lot.minStockOverride ?? part.minStockGlobal ?? 0
 *   isLowStock   = lot.quantityOnHand < effectiveMin
 */
export interface StockLot {
  id: string;
  partId: string;
  vesselId: string;
  location: string;
  /** Current on-hand quantity. Updated by movements only. */
  quantityOnHand: number;
  /** null = inherit Part.minStockGlobal. */
  minStockOverride: number | null;
  /** null = inherit Part.maxStockGlobal. */
  maxStockOverride: number | null;
  /** ISO timestamp of the most recent movement, or null if never moved. */
  lastMovementAt: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

/** Filters accepted by `GET /stock-lots`. */
export interface StockLotFilters {
  vesselId?: string;
  partId?: string;
  location?: string;
  /**
   * Server-side low-stock filter. The backend computes its own
   * threshold. The frontend ALSO computes lowStock client-side for
   * row-level highlighting because we want every row to know whether
   * it crosses the threshold, not just the filtered subset.
   */
  lowStockOnly?: boolean;
}

/**
 * Resolve the effective minimum stock for a lot, falling back to the
 * parent Part.minStockGlobal then to 0 if neither is set.
 */
export function resolveMinStock(
  lot: Pick<StockLot, 'minStockOverride'>,
  partMinGlobal: number | null | undefined,
): number {
  return lot.minStockOverride ?? partMinGlobal ?? 0;
}

/** Same logic for the maximum. null+null+null = no cap (Number.POSITIVE_INFINITY). */
export function resolveMaxStock(
  lot: Pick<StockLot, 'maxStockOverride'>,
  partMaxGlobal: number | null | undefined,
): number {
  return lot.maxStockOverride ?? partMaxGlobal ?? Number.POSITIVE_INFINITY;
}

/** True when current quantity is strictly below the effective minimum. */
export function isLowStock(
  lot: Pick<StockLot, 'minStockOverride' | 'quantityOnHand'>,
  partMinGlobal: number | null | undefined,
): boolean {
  return lot.quantityOnHand < resolveMinStock(lot, partMinGlobal);
}

/** True when current quantity strictly exceeds the effective maximum. */
export function isOverstock(
  lot: Pick<StockLot, 'maxStockOverride' | 'quantityOnHand'>,
  partMaxGlobal: number | null | undefined,
): boolean {
  const max = resolveMaxStock(lot, partMaxGlobal);
  if (!Number.isFinite(max)) return false;
  return lot.quantityOnHand > max;
}
